<template>
  <div class="min-h-screen bg-gray-100">
    <div class="mx-auto p-6">
      <div
        class="flex justify-between items-end mb-6 mdmax:flex-col mdmax:items-stretch gap-4"
      >
        <div class="flex items-center gap-2">
          <button
            class="px-4 py-2 rounded-lg font-medium"
            :class="
              activeTab === 'customers'
                ? 'bg-primary-500 text-white'
                : 'bg-white text-neutral-700'
            "
            @click="activeTab = 'customers'"
          >
            Customers
          </button>
          <button
            class="px-4 py-2 rounded-lg font-medium"
            :disabled="!selectedCustomer"
            :class="
              activeTab === 'prices'
                ? 'bg-primary-500 text-white'
                : 'bg-white text-neutral-700'
            "
            @click="openPricesTab()"
          >
            Prices
          </button>
        </div>

        <div class="flex-1 mdmax:w-full">
          <div class="max-w-lg" v-if="activeTab === 'customers'">
            <label
              class="text-base text-neutral-700 font-medium mb-1 inline-block"
            >
              Pencarian
            </label>
            <div class="relative">
              <TextField
                type="text"
                :withIcon="true"
                placeholder="Cari customer..."
                v-model="customerSearch"
                @enter="onSearchCustomers"
              />
              <div class="absolute left-3 top-1/2 transform -translate-y-1/2">
                <Search />
              </div>
            </div>
          </div>

          <div class="max-w-lg" v-else>
            <label
              class="text-base text-neutral-700 font-medium mb-1 inline-block"
            >
              Destination (Opsional)
            </label>
            <div class="relative">
              <TextField
                type="text"
                :withIcon="true"
                placeholder="Filter destination..."
                v-model="destinationFilter"
                @enter="onSearchPrices"
              />
              <div class="absolute left-3 top-1/2 transform -translate-y-1/2">
                <Search />
              </div>
            </div>
          </div>
        </div>

        <div class="flex mdmax:w-full justify-end gap-2">
          <button
            v-if="activeTab === 'customers'"
            @click="openCustomerModal('create')"
            class="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 font-medium"
          >
            + Tambah Customer
          </button>
          <button
            v-else
            :disabled="!selectedCustomer"
            @click="openPriceModal('create')"
            class="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            + Tambah Price
          </button>
        </div>
      </div>

      <div v-if="activeTab === 'customers'">
        <TableComponent :columns="customerColumns" :list="customerTableData">
          <template #table-row="{ entry, column }">
            <span v-if="column.field === 'action'" class="flex gap-2">
              <button
                @click="selectCustomer(entry)"
                class="px-3 py-1 rounded bg-neutral-200 text-neutral-700 hover:bg-neutral-300 text-sm"
                title="Kelola harga"
              >
                Prices
              </button>
              <button
                @click="openCustomerModal('edit', entry)"
                class="px-3 py-1 rounded bg-neutral-200 text-neutral-700 hover:bg-neutral-300 text-sm"
                title="Edit"
              >
                Edit
              </button>
              <button
                @click="openDeleteCustomerModal(entry)"
                class="px-3 py-1 rounded bg-red-500 text-white hover:bg-red-600 text-sm"
                title="Delete"
              >
                Delete
              </button>
            </span>
            <span v-else-if="column.field === 'RowNumber'" class="font-medium">
              {{ entry.RowNumber }}
            </span>
            <span v-else-if="column.field === 'discount_percent'">
              {{ entry.discount_percent || "0" }}%
            </span>
            <span v-else>{{ entry[column.field] || "-" }}</span>
          </template>
        </TableComponent>

        <div
          v-if="customerTableData.length > 0"
          class="mt-6 mb-6 flex justify-between items-center mdmax:flex-col mdmax:items-start gap-4"
        >
          <div v-if="!isMobile" class="text-sm font-medium text-neutral-500">
            Menampilkan {{ customerStart }} - {{ customerEnd }} dari
            {{ totalCustomers }}
          </div>
          <Pagination
            :currentPage="customerPage"
            :totalCount="totalCustomers"
            :pageSize="customerPerPage"
            @onPageChange="changeCustomerPage"
          />
        </div>
      </div>

      <div v-else>
        <div class="bg-white rounded-lg p-4 mb-4 shadow-sm">
          <p class="text-neutral-500 text-sm">Selected Customer</p>
          <p class="text-neutral-800 font-bold">
            {{ selectedCustomer?.customer_name || "-" }}
          </p>
          <p class="text-neutral-500 text-sm">
            {{ selectedCustomer?.customer_address || "-" }}
          </p>
        </div>

        <TableComponent :columns="priceColumns" :list="priceTableData">
          <template #table-row="{ entry, column }">
            <span v-if="column.field === 'action'" class="flex gap-2">
              <button
                @click="openPriceModal('edit', entry)"
                class="px-3 py-1 rounded bg-neutral-200 text-neutral-700 hover:bg-neutral-300 text-sm"
                title="Edit"
              >
                Edit
              </button>
              <button
                @click="openDeletePriceModal(entry)"
                class="px-3 py-1 rounded bg-red-500 text-white hover:bg-red-600 text-sm"
                title="Delete"
              >
                Delete
              </button>
            </span>
            <span v-else-if="column.field === 'RowNumber'" class="font-medium">
              {{ entry.RowNumber }}
            </span>
            <span v-else-if="column.field === 'min_weight'">
              {{ entry.min_weight ?? 0 }}
            </span>
            <span v-else-if="column.field === 'price'">
              {{ formatCurrency(entry.price) }}
            </span>
            <span v-else>{{ entry[column.field] || "-" }}</span>
          </template>
        </TableComponent>

        <div
          v-if="priceTableData.length > 0"
          class="mt-6 mb-6 flex justify-between items-center mdmax:flex-col mdmax:items-start gap-4"
        >
          <div v-if="!isMobile" class="text-sm font-medium text-neutral-500">
            Menampilkan {{ priceStart }} - {{ priceEnd }} dari
            {{ totalPrices }}
          </div>
          <Pagination
            :currentPage="pricePage"
            :totalCount="totalPrices"
            :pageSize="pricePerPage"
            @onPageChange="changePricePage"
          />
        </div>
      </div>

      <Modal
        :modalVisible="customerModalVisible"
        @close="closeCustomerModal"
        width="960px"
      >
        <div slot="modal-content" class="text-left">
          <h3 class="text-lg font-bold text-neutral-800 mb-4">
            {{ customerModalMode === "create" ? "Tambah" : "Edit" }} Customer
          </h3>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="md:col-span-2">
              <TextField
                label="Customer Name"
                required
                v-model="customerForm.customer_name"
                placeholder="Nama customer"
              />
            </div>
            <div class="md:col-span-2">
              <TextField
                label="NPWP"
                v-model="customerForm.npwp"
                placeholder="Masukkan NPWP"
              />
            </div>
            <div class="md:col-span-2">
              <Textarea
                label="Customer Address"
                v-model="customerForm.customer_address"
                placeholder="Alamat customer"
              />
            </div>
            <div>
              <TextField
                label="PIC Name"
                v-model="customerForm.pic_name"
                placeholder="Nama PIC"
              />
            </div>
            <div>
              <TextField
                label="Discount Percent"
                type="number"
                v-model="customerForm.discount_percent"
                placeholder="Contoh: 5"
              />
            </div>
            <div class="md:col-span-2">
              <Textarea
                label="Payment Terms"
                v-model="customerForm.payment_terms"
                placeholder="Contoh: 14 hari"
                heightClass="h-24"
              />
            </div>
            <div class="md:col-span-2 flex items-center gap-2">
              <input
                id="is-rounded"
                type="checkbox"
                class="w-4 h-4"
                v-model="customerForm.is_rounded"
              />
              <label for="is-rounded" class="text-sm text-neutral-700">
                Is Rounded
              </label>
            </div>
          </div>

          <div class="flex justify-end gap-3 mt-6">
            <button
              @click="closeCustomerModal"
              class="px-4 py-2 rounded-lg border border-neutral-300 text-neutral-600 hover:bg-neutral-50 font-medium"
            >
              Batal
            </button>
            <button
              @click="submitCustomer"
              :disabled="!customerForm.customer_name"
              class="px-4 py-2 rounded-lg bg-primary-500 text-white hover:bg-primary-600 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Simpan
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        :modalVisible="deleteCustomerModalVisible"
        @close="closeDeleteCustomerModal"
        width="400px"
      >
        <div slot="modal-content" class="text-center">
          <h3 class="text-lg font-bold text-neutral-800 mb-2">
            Hapus Customer
          </h3>
          <p class="text-neutral-600 mb-6">
            Apakah Anda yakin ingin menghapus customer
            <strong>{{ customerToDelete?.customer_name }}</strong
            >?
          </p>
          <div class="flex justify-center gap-3">
            <button
              @click="closeDeleteCustomerModal"
              class="px-4 py-2 rounded-lg border border-neutral-300 text-neutral-600 hover:bg-neutral-50 font-medium"
            >
              Batal
            </button>
            <button
              @click="confirmDeleteCustomer"
              class="px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 font-medium"
            >
              Hapus
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        :modalVisible="priceModalVisible"
        @close="closePriceModal"
        width="520px"
      >
        <div slot="modal-content" class="text-left">
          <h3 class="text-lg font-bold text-neutral-800 mb-4">
            {{ priceModalMode === "create" ? "Tambah" : "Edit" }} Price
          </h3>

          <div class="grid grid-cols-1 gap-4">
            <TextField
              label="Destination"
              required
              v-model="priceForm.destination"
              placeholder="Contoh: JKT"
            />
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextField
                label="Min Weight"
                type="number"
                v-model="priceForm.min_weight"
                placeholder="0"
              />
              <TextField
                label="Price"
                type="number"
                v-model="priceForm.price"
                placeholder="25000"
              />
            </div>
          </div>

          <div class="flex justify-end gap-3 mt-6">
            <button
              @click="closePriceModal"
              class="px-4 py-2 rounded-lg border border-neutral-300 text-neutral-600 hover:bg-neutral-50 font-medium"
            >
              Batal
            </button>
            <button
              @click="submitPrice"
              :disabled="!priceForm.destination || !selectedCustomer"
              class="px-4 py-2 rounded-lg bg-primary-500 text-white hover:bg-primary-600 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Simpan
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        :modalVisible="deletePriceModalVisible"
        @close="closeDeletePriceModal"
        width="400px"
      >
        <div slot="modal-content" class="text-center">
          <h3 class="text-lg font-bold text-neutral-800 mb-2">Hapus Price</h3>
          <p class="text-neutral-600 mb-6">
            Apakah Anda yakin ingin menghapus price untuk destination
            <strong>{{ priceToDelete?.destination }}</strong
            >?
          </p>
          <div class="flex justify-center gap-3">
            <button
              @click="closeDeletePriceModal"
              class="px-4 py-2 rounded-lg border border-neutral-300 text-neutral-600 hover:bg-neutral-50 font-medium"
            >
              Batal
            </button>
            <button
              @click="confirmDeletePrice"
              class="px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 font-medium"
            >
              Hapus
            </button>
          </div>
        </div>
      </Modal>
    </div>
  </div>
</template>

<script>
import api from "@/api"
import { mapActions, mapGetters } from "vuex"
import TableComponent from "@/components/Table/Table"
import Pagination from "@/components/Pagination/Pagination"
import TextField from "@/components/Forms/TextField"
import Textarea from "@/components/Forms/Textarea"
import Modal from "@/components/Modal/Modal"
import Search from "@/components/Icons/Search"

export default {
  name: "InvoicesCustomers",
  components: {
    TableComponent,
    Pagination,
    TextField,
    Textarea,
    Modal,
    Search
  },
  data() {
    return {
      activeTab: "customers",

      customerColumns: [
        { label: "No", field: "RowNumber" },
        { label: "Customer Name", field: "customer_name" },
        { label: "PIC", field: "pic_name" },
        { label: "Payment Terms", field: "payment_terms" },
        { label: "Discount", field: "discount_percent" },
        { label: "Action", field: "action" }
      ],
      customerSearch: "",
      customers: [],
      customersMeta: {},
      customerPage: 1,
      customerPerPage: 10,

      selectedCustomer: null,

      customerModalVisible: false,
      customerModalMode: "create",
      customerForm: {
        customer_name: "",
        npwp: "",
        customer_address: "",
        pic_name: "",
        payment_terms: "",
        is_rounded: false,
        discount_percent: ""
      },
      customerToEdit: null,

      deleteCustomerModalVisible: false,
      customerToDelete: null,

      priceColumns: [
        { label: "No", field: "RowNumber" },
        { label: "Destination", field: "destination" },
        { label: "Min Weight", field: "min_weight" },
        { label: "Price", field: "price" },
        { label: "Action", field: "action" }
      ],
      destinationFilter: "",
      prices: [],
      pricesMeta: {},
      pricePage: 1,
      pricePerPage: 10,

      priceModalVisible: false,
      priceModalMode: "create",
      priceForm: {
        destination: "",
        min_weight: "0",
        price: ""
      },
      priceToEdit: null,

      deletePriceModalVisible: false,
      priceToDelete: null
    }
  },
  computed: {
    ...mapGetters("common", ["isMobile"]),
    totalCustomers() {
      return (
        this.customersMeta?.total_item ||
        this.customersMeta?.total ||
        this.customersMeta?.totalItems ||
        0
      )
    },
    customerTableData() {
      return (this.customers || []).map((customer, index) => ({
        ...customer,
        RowNumber: (this.customerPage - 1) * this.customerPerPage + index + 1
      }))
    },
    customerStart() {
      return this.totalCustomers
        ? (this.customerPage - 1) * this.customerPerPage + 1
        : 0
    },
    customerEnd() {
      return Math.min(this.customerPage * this.customerPerPage, this.totalCustomers)
    },

    totalPrices() {
      return (
        this.pricesMeta?.total_item ||
        this.pricesMeta?.total ||
        this.pricesMeta?.totalItems ||
        0
      )
    },
    priceTableData() {
      return (this.prices || []).map((price, index) => ({
        ...price,
        RowNumber: (this.pricePage - 1) * this.pricePerPage + index + 1
      }))
    },
    priceStart() {
      return this.totalPrices ? (this.pricePage - 1) * this.pricePerPage + 1 : 0
    },
    priceEnd() {
      return Math.min(this.pricePage * this.pricePerPage, this.totalPrices)
    }
  },
  methods: {
    ...mapActions("common", ["showLoading", "hideLoading", "setToast"]),
    unwrapList(envelope) {
      const data = envelope?.data?.data
      const meta = envelope?.data?.meta || envelope?.data?.data?.meta || {}
      const items = Array.isArray(data) ? data : data?.items || data?.data || []
      return { items, meta }
    },
    toastSuccess(message, secondaryText = "") {
      this.setToast({
        options: {
          type: "success",
          duration: 2500,
          primaryText: message,
          secondaryText
        }
      })
    },
    toastError(message, secondaryText = "") {
      this.setToast({
        options: {
          type: "danger",
          duration: 3500,
          primaryText: message,
          secondaryText
        }
      })
    },
    getErrorMessage(err, fallback = "Terjadi kesalahan") {
      return (
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        fallback
      )
    },
    toDecimalString(value, fallback = "0") {
      const raw = String(value ?? "").trim()
      if (!raw) return fallback
      const normalized = raw.replace(/,/g, ".").replace(/[^0-9.-]/g, "")
      return normalized || fallback
    },
    formatCurrency(value) {
      const numberValue = Number(this.toDecimalString(value, "0"))
      if (Number.isNaN(numberValue)) return "Rp 0"
      return new Intl.NumberFormat("id-ID", {
        style: "currency",
        currency: "IDR",
        maximumFractionDigits: 0
      }).format(numberValue)
    },

    async fetchCustomers() {
      this.showLoading()
      try {
        const res = await api.invoiceGenerator.customers({
          page: this.customerPage,
          per_page: this.customerPerPage,
          search: (this.customerSearch || "").trim()
        })
        const { items, meta } = this.unwrapList(res)
        this.customers = items
        this.customersMeta = meta || {}
      } catch (err) {
        this.toastError(this.getErrorMessage(err, "Gagal memuat customer"))
      } finally {
        this.hideLoading()
      }
    },

    onSearchCustomers() {
      this.customerPage = 1
      this.fetchCustomers()
    },
    changeCustomerPage(page) {
      this.customerPage = page
      this.fetchCustomers()
    },
    selectCustomer(customer) {
      this.selectedCustomer = customer
      this.openPricesTab()
    },
    openPricesTab() {
      if (!this.selectedCustomer) {
        this.toastError("Pilih customer terlebih dahulu")
        return
      }
      this.activeTab = "prices"
      this.pricePage = 1
      this.destinationFilter = ""
      this.fetchPrices()
    },

    openCustomerModal(mode, customer) {
      this.customerModalMode = mode
      this.customerToEdit = mode === "edit" ? customer : null
      this.customerForm = {
        customer_name: customer?.customer_name || "",
        npwp: customer?.npwp || "",
        customer_address: customer?.customer_address || "",
        pic_name: customer?.pic_name || "",
        payment_terms:
          customer?.payment_terms === 0 || customer?.payment_terms
            ? String(customer.payment_terms)
            : "",
        is_rounded: !!customer?.is_rounded,
        discount_percent:
          customer?.discount_percent === 0 || customer?.discount_percent
            ? String(customer.discount_percent)
            : ""
      }
      this.customerModalVisible = true
    },
    closeCustomerModal() {
      this.customerModalVisible = false
      this.customerToEdit = null
    },
    async submitCustomer() {
      if (!this.customerForm.customer_name) return

      const payloads = {
        customer_name: this.customerForm.customer_name,
        npwp: this.customerForm.npwp || "",
        customer_address: this.customerForm.customer_address,
        pic_name: this.customerForm.pic_name,
        payment_terms: this.customerForm.payment_terms || "",
        is_rounded: !!this.customerForm.is_rounded,
        discount_percent: this.toDecimalString(
          this.customerForm.discount_percent,
          "0"
        )
      }

      this.showLoading()
      try {
        let res
        if (this.customerModalMode === "create") {
          res = await api.invoiceGenerator.createCustomer(payloads)
        } else {
          const id = this.customerToEdit?.id || this.customerToEdit?.customer_id
          res = await api.invoiceGenerator.updateCustomer(id, payloads)
        }
        this.toastSuccess(res?.data?.message || "Berhasil menyimpan customer")
        this.closeCustomerModal()
        await this.fetchCustomers()
        if (this.selectedCustomer && this.customerModalMode === "edit") {
          const updated = this.customers.find(
            (c) =>
              c.id === this.selectedCustomer.id ||
              c.customer_id === this.selectedCustomer.customer_id
          )
          if (updated) this.selectedCustomer = updated
        }
      } catch (err) {
        this.toastError(this.getErrorMessage(err, "Gagal menyimpan customer"))
      } finally {
        this.hideLoading()
      }
    },

    openDeleteCustomerModal(customer) {
      this.customerToDelete = customer
      this.deleteCustomerModalVisible = true
    },
    closeDeleteCustomerModal() {
      this.deleteCustomerModalVisible = false
      this.customerToDelete = null
    },
    async confirmDeleteCustomer() {
      if (!this.customerToDelete) return
      const id = this.customerToDelete.id || this.customerToDelete.customer_id

      this.showLoading()
      try {
        const res = await api.invoiceGenerator.deleteCustomer(id)
        this.toastSuccess(res?.data?.message || "Customer berhasil dihapus")
        this.closeDeleteCustomerModal()
        if (
          this.selectedCustomer &&
          (this.selectedCustomer.id === id ||
            this.selectedCustomer.customer_id === id)
        ) {
          this.selectedCustomer = null
          this.activeTab = "customers"
        }
        await this.fetchCustomers()
      } catch (err) {
        const message =
          err?.response?.status === 422
            ? this.getErrorMessage(err, "Customer tidak bisa dihapus")
            : this.getErrorMessage(err, "Gagal menghapus customer")
        this.toastError(message)
      } finally {
        this.hideLoading()
      }
    },

    async fetchPrices() {
      if (!this.selectedCustomer) return
      const customerId =
        this.selectedCustomer.id || this.selectedCustomer.customer_id

      this.showLoading()
      try {
        const res = await api.invoiceGenerator.customerPrices({
          page: this.pricePage,
          per_page: this.pricePerPage,
          customer_id: customerId,
          destination: this.destinationFilter || undefined
        })
        const { items, meta } = this.unwrapList(res)
        this.prices = items
        this.pricesMeta = meta || {}
      } catch (err) {
        this.toastError(this.getErrorMessage(err, "Gagal memuat prices"))
      } finally {
        this.hideLoading()
      }
    },
    onSearchPrices() {
      this.pricePage = 1
      this.fetchPrices()
    },
    changePricePage(page) {
      this.pricePage = page
      this.fetchPrices()
    },

    openPriceModal(mode, price) {
      if (!this.selectedCustomer) return
      this.priceModalMode = mode
      this.priceToEdit = mode === "edit" ? price : null
      this.priceForm = {
        destination: price?.destination || "",
        min_weight:
          price?.min_weight === 0 || price?.min_weight
            ? String(price.min_weight)
            : "0",
        price:
          price?.price === 0 || price?.price ? String(price.price) : ""
      }
      this.priceModalVisible = true
    },
    closePriceModal() {
      this.priceModalVisible = false
      this.priceToEdit = null
    },
    async submitPrice() {
      if (!this.selectedCustomer || !this.priceForm.destination) return
      const customerId =
        this.selectedCustomer.id || this.selectedCustomer.customer_id

      const payloads = {
        invoice_customer_id: customerId,
        destination: this.priceForm.destination,
        min_weight: this.toDecimalString(this.priceForm.min_weight, "0"),
        price: this.toDecimalString(this.priceForm.price, "0")
      }

      this.showLoading()
      try {
        let res
        if (this.priceModalMode === "create") {
          res = await api.invoiceGenerator.createCustomerPrice(payloads)
        } else {
          const id = this.priceToEdit?.id || this.priceToEdit?.price_id
          res = await api.invoiceGenerator.updateCustomerPrice(id, payloads)
        }
        this.toastSuccess(res?.data?.message || "Berhasil menyimpan price")
        this.closePriceModal()
        await this.fetchPrices()
      } catch (err) {
        this.toastError(this.getErrorMessage(err, "Gagal menyimpan price"))
      } finally {
        this.hideLoading()
      }
    },

    openDeletePriceModal(price) {
      this.priceToDelete = price
      this.deletePriceModalVisible = true
    },
    closeDeletePriceModal() {
      this.deletePriceModalVisible = false
      this.priceToDelete = null
    },
    async confirmDeletePrice() {
      if (!this.priceToDelete) return
      const id = this.priceToDelete.id || this.priceToDelete.price_id

      this.showLoading()
      try {
        const res = await api.invoiceGenerator.deleteCustomerPrice(id)
        this.toastSuccess(res?.data?.message || "Price berhasil dihapus")
        this.closeDeletePriceModal()
        await this.fetchPrices()
      } catch (err) {
        this.toastError(this.getErrorMessage(err, "Gagal menghapus price"))
      } finally {
        this.hideLoading()
      }
    }
  },
  mounted() {
    this.fetchCustomers()
  }
}
</script>
